import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Eye, Settings, Download, Upload, FileImage } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import TemplateEditor from '../../components/TemplateEditor';
import api from '../../services/api';

const GerenciarTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [isCreatingNew, setIsCreatingNew] = useState(false);

  useEffect(() => {
    carregarTemplates();
  }, []);

  const carregarTemplates = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/templates');
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Erro ao carregar templates:', error);
      alert('Erro ao carregar templates');
    } finally {
      setLoading(false);
    }
  };

  const handleCriarTemplate = () => {
    setSelectedTemplate(null);
    setIsCreatingNew(true);
    setShowEditor(true);
  };

  const handleEditarTemplate = async (template) => {
    try {
      // Buscar dados completos do template
      const response = await api.get(`/admin/templates/${template.id}`);
      setSelectedTemplate(response.data.template);
      setIsCreatingNew(false);
      setShowEditor(true);
    } catch (error) {
      console.error('Erro ao carregar template:', error);
      alert('Erro ao carregar template');
    }
  };

  const handleDeletarTemplate = async (templateId) => {
    if (!window.confirm('Tem certeza que deseja deletar este template?')) {
      return;
    }

    try {
      await api.delete(`/admin/templates/${templateId}`);
      alert('Template deletado com sucesso!');
      carregarTemplates();
    } catch (error) {
      console.error('Erro ao deletar template:', error);
      alert('Erro ao deletar template');
    }
  };

  const handleToggleAtivo = async (template) => {
    try {
      await api.put(`/admin/templates/${template.id}`, {
        ativo: !template.ativo
      });
      alert(`Template ${template.ativo ? 'desativado' : 'ativado'} com sucesso!`);
      carregarTemplates();
    } catch (error) {
      console.error('Erro ao atualizar template:', error);
      alert('Erro ao atualizar template');
    }
  };

  const formatarData = (data) => {
    return new Date(data).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500"></div>
      </div>
    );
  }

  if (showEditor) {
    return (
      <TemplateEditor
        template={selectedTemplate}
        isCreating={isCreatingNew}
        onClose={() => {
          setShowEditor(false);
          setSelectedTemplate(null);
          carregarTemplates();
        }}
      />
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-white">Templates de Certificados</h1>
          <p className="text-slate-300 mt-2">Gerencie os modelos de certificados disponíveis</p>
        </div>
        <Button 
          onClick={handleCriarTemplate}
          className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800"
        >
          <Plus className="w-4 h-4 mr-2" />
          Criar Template
        </Button>
      </div>

      {/* Lista de Templates */}
      {templates.length === 0 ? (
        <Card className="p-12 bg-slate-800/50 border-slate-700/50 text-center">
          <FileImage className="w-16 h-16 text-slate-400 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-white mb-2">Nenhum template encontrado</h3>
          <p className="text-slate-400 mb-6">Crie seu primeiro template de certificado</p>
          <Button 
            onClick={handleCriarTemplate}
            className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800"
          >
            <Plus className="w-4 h-4 mr-2" />
            Criar Primeiro Template
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {templates.map((template) => (
            <Card key={template.id} className="bg-slate-800/50 border-slate-700/50 backdrop-blur-sm hover:bg-slate-800/70 transition-all duration-200">
              <CardContent className="p-0">
                {/* Preview do Template */}
                <div className="relative h-48 bg-gradient-to-br from-slate-700 to-slate-800 rounded-t-lg flex items-center justify-center">
                  {template.thumbnail ? (
                    <img 
                      src={`http://localhost:5000/uploads/${template.thumbnail}`}
                      alt={template.nome}
                      className="w-full h-full object-cover rounded-t-lg"
                    />
                  ) : (
                    <div className="text-center">
                      <FileImage className="w-12 h-12 text-slate-400 mx-auto mb-2" />
                      <p className="text-slate-400 text-sm">Sem preview</p>
                    </div>
                  )}
                  
                  {/* Badge de Status */}
                  <div className="absolute top-3 right-3">
                    <Badge 
                      variant={template.ativo ? "default" : "secondary"}
                      className={template.ativo 
                        ? "bg-green-600/80 text-white" 
                        : "bg-slate-600/80 text-slate-300"
                      }
                    >
                      {template.ativo ? 'Ativo' : 'Inativo'}
                    </Badge>
                  </div>

                  {/* Badge de Orientação */}
                  <div className="absolute top-3 left-3">
                    <Badge className="bg-slate-900/80 border-slate-600 text-slate-300">
                      {template.orientation === 'horizontal' ? 'Horizontal' : 'Vertical'}
                    </Badge>
                  </div>
                </div>

                {/* Informações do Template */}
                <div className="p-6">
                  <div className="mb-4">
                    <h3 className="text-lg font-bold text-white mb-1">{template.nome}</h3>
                    {template.descricao && (
                      <p className="text-slate-400 text-sm line-clamp-2">{template.descricao}</p>
                    )}
                  </div>

                  {/* Dimensões */}
                  <div className="flex items-center justify-between text-sm text-slate-400 mb-4">
                    <span>Dimensões:</span>
                    <span>{template.width} × {template.height}px</span>
                  </div>

                  {/* Data de Criação */}
                  <div className="flex items-center justify-between text-sm text-slate-400 mb-6">
                    <span>Criado:</span>
                    <span>{formatarData(template.created_at)}</span>
                  </div>

                  {/* Ações */}
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => handleEditarTemplate(template)}
                      className="flex-1 bg-blue-600/20 hover:bg-blue-600/30 text-blue-400 border border-blue-600/50"
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Editar
                    </Button>
                    
                    <Button
                      size="sm"
                      onClick={() => handleToggleAtivo(template)}
                      className={`border ${
                        template.ativo 
                          ? 'bg-orange-600/20 hover:bg-orange-600/30 text-orange-400 border-orange-600/50' 
                          : 'bg-green-600/20 hover:bg-green-600/30 text-green-400 border-green-600/50'
                      }`}
                    >
                      <Settings className="w-4 h-4" />
                    </Button>

                    <Button
                      size="sm"
                      onClick={() => handleDeletarTemplate(template.id)}
                      className="bg-red-600/20 hover:bg-red-600/30 text-red-400 border border-red-600/50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};



export default GerenciarTemplates; 